import { useMemo } from "react";

import type Highcharts from "highcharts";
import { renderToStaticMarkup } from "react-dom/server";
import styled from "styled-components";

import { Map } from "@similarweb/pro-logic-components";

import { useMapGeoJson } from "app/common/hooks";
import { percentageSignFilter } from "app/filters/ngFilters";
import { WidgetContainer } from "app/components/React/WidgetContainer/WidgetContainer";
import { useTranslation } from "pro-features/components/WithTranslation/src/I18n";
import { useSubtitleFilters } from "app/custom-hooks/useSubtitleFilters";

import { useCategoriesComparison } from "../../categories-context";
import { useGeographyMapData } from "../hooks/useGeographyMapData";
import { isComparisonMapDataItem } from "../types";

import { ComparisonMapTooltip, SingleMapTooltip } from "./IndustryAnalysisGeographyMapWidgetTooltips";

const CHART_HEIGHT = 450;
const valueFormatter = percentageSignFilter();

const MapWidgetContainer = styled(WidgetContainer)`
    margin-bottom: 24px;
`;

export const IndustryAnalysisGeographyMapWidget = () => {
    const t = useTranslation();
    const { isComparisonMode, categories } = useCategoriesComparison();
    const subtitleFilters = useSubtitleFilters("date", "webSource");

    const { mapData } = useGeographyMapData();
    const { data: mapGeoJson, isLoading: isMapGeoJsonLoading } = useMapGeoJson();

    const chartConfig: Highcharts.Options = useMemo(() => {
        return {
            chart: {
                height: CHART_HEIGHT,
                animation: false,
                margin: [24, 24, 24, 24],
            },
            title: {
                text: null,
            },
            tooltip: {
                useHTML: true,
                formatter: function () {
                    const trafficShareText = t("industry.analysis.geo.overtime.map.widget.tooltip", {
                        value: valueFormatter(this.value, 2),
                    });

                    const shareValuesByIndustry = mapData?.find((item) => item["hc-key"] === this["hc-key"]);

                    if (isComparisonMode && shareValuesByIndustry && isComparisonMapDataItem(shareValuesByIndustry)) {
                        const primaryIndustry = {
                            name: categories[0]?.category.text || "N/A",
                            share: valueFormatter(shareValuesByIndustry.primaryValue, 2) || t("common.na"),
                        };
                        const comparedIndustry = {
                            name: categories[1]?.category.text || "N/A",
                            share: valueFormatter(shareValuesByIndustry.comparedValue, 2) || t("common.na"),
                        };
                        return renderToStaticMarkup(
                            <ComparisonMapTooltip
                                countryName={this.name || "N/A"}
                                primaryIndustry={primaryIndustry}
                                comparedIndustry={comparedIndustry}
                            />,
                        );
                    }

                    return renderToStaticMarkup(
                        <SingleMapTooltip
                            countryName={this.name || "N/A"}
                            trafficShareText={trafficShareText}
                        />,
                    );
                },
            },
        };
    }, [categories, isComparisonMode, mapData, t]);

    return (
        <MapWidgetContainer
            withBorder
            title={t("industry.analysis.geo.overtime.map.widget.title")}
            tooltip={t("industry.analysis.geo.overtime.map.widget.tooltip.info")}
            subtitleFilters={subtitleFilters}
            minHeight={540}
            headerBadge="new"
        >
            <Map
                config={chartConfig}
                mapData={mapGeoJson}
                data={mapData}
                isLoading={isMapGeoJsonLoading}
                style={{ height: "100%", width: "100%" }}
            />
        </MapWidgetContainer>
    );
};
